import React, { useMemo, useState } from "react";
import { Calculator, RotateCcw, TrendingUp, Banknote, Percent, Sparkles } from "lucide-react";
import { SectionCard } from "@/components/Shared";

// Base assumptions (ZAR millions, 2024 actuals)
// Note: valuation is derived from ebitda × multiplier so base preset shows zero delta.
const BASE_EBITDA = 1148;
const BASE_MULTIPLIER = 5.0;
const BASE = {
  revenue: 3415.0,
  opex: 3415.0 - BASE_EBITDA, // 2267
  ebitda: BASE_EBITDA,
  margin: (BASE_EBITDA / 3415.0) * 100,
  multiplier: BASE_MULTIPLIER,
  valuation: BASE_EBITDA * BASE_MULTIPLIER, // 5740
  energyShareOfOpex: 0.24,
  variableOpexShare: 0.60,
  irrBase: 22.8,
};

function fmtUsd(v) {
  if (Math.abs(v) >= 1000) return `R${(v / 1000).toFixed(2)}bn`;
  return `R${Math.round(v).toLocaleString()}m`;
}

function Slider({ label, value, onChange, max, step, color, hint, testid }) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">{label}</label>
        <span
          className="font-display font-bold tabular-nums"
          style={{ color, fontSize: "1.25rem" }}
          data-testid={`${testid}-value`}
        >
          +{value}%
        </span>
      </div>
      <input
        type="range"
        min={0}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testid}
        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-amber-700"
        style={{
          background: `linear-gradient(to right, ${color} 0%, ${color} ${(value / max) * 100}%, #E2E8F0 ${(value / max) * 100}%, #E2E8F0 100%)`,
        }}
      />
      <div className="flex justify-between text-[10px] text-slate-400 font-mono">
        <span>0%</span>
        <span>{hint}</span>
        <span>{max}%</span>
      </div>
    </div>
  );
}

export default function RoiCalculator() {
  const [uptime, setUptime] = useState(6.6); // pts uplift -> treated as % production hours
  const [energy, setEnergy] = useState(18); // % reduction on energy spend
  const [throughput, setThroughput] = useState(12); // % more tons/yr (debottleneck)

  const computed = useMemo(() => {
    // Revenue scales with production hours (uptime) and throughput per hour (debottleneck).
    // Simplifying — treat both as compounding multipliers.
    const productionMultiplier = (1 + uptime / 100) * (1 + throughput / 100);
    const newRevenue = BASE.revenue * productionMultiplier;

    // Energy savings: energyShareOfOpex of base opex × energy reduction
    const energySaving = BASE.opex * BASE.energyShareOfOpex * (energy / 100);

    // Variable opex grows with production; fixed portion stays flat.
    const variableOpexBase = BASE.opex * BASE.variableOpexShare;
    const fixedOpex = BASE.opex - variableOpexBase;
    const newVariableOpex = variableOpexBase * productionMultiplier;
    const newOpex = fixedOpex + newVariableOpex - energySaving;

    const newEbitda = newRevenue - newOpex;
    const newMargin = (newEbitda / newRevenue) * 100;
    const newValuation = newEbitda * BASE.multiplier;
    const valuationUplift = newValuation - BASE.valuation;
    const ebitdaUplift = newEbitda - BASE.ebitda;
    const valuationUpliftPct = (valuationUplift / BASE.valuation) * 100;
    // IRR scales loosely with valuation upside vs investment; cap at 32%
    const newIrr = Math.min(32, BASE.irrBase + valuationUpliftPct * 0.18);

    return {
      newRevenue,
      newOpex,
      newEbitda,
      newMargin,
      newValuation,
      valuationUplift,
      ebitdaUplift,
      newIrr,
    };
  }, [uptime, energy, throughput]);

  const reset = () => {
    setUptime(0);
    setEnergy(0);
    setThroughput(0);
  };

  const setPreset = (preset) => {
    if (preset === "base") { setUptime(0); setEnergy(0); setThroughput(0); }
    if (preset === "moderate") { setUptime(3); setEnergy(10); setThroughput(6); }
    if (preset === "ambitious") { setUptime(6.6); setEnergy(18); setThroughput(12); }
    if (preset === "stretch") { setUptime(10); setEnergy(25); setThroughput(18); }
  };

  return (
    <SectionCard
      title="Interactive ROI Calculator"
      description="Move the sliders to model how operational improvements compound into EBITDA, IRR and valuation"
      action={<Calculator size={16} className="text-amber-700" />}
    >
      <div data-testid="roi-calculator" className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* Sliders */}
        <div className="xl:col-span-2 space-y-6">
          <Slider
            label="Uptime improvement"
            value={uptime}
            onChange={setUptime}
            max={15}
            step={0.1}
            color="#0F172A"
            hint="more productive hours / yr"
            testid="roi-slider-uptime"
          />
          <Slider
            label="Energy cost reduction"
            value={energy}
            onChange={setEnergy}
            max={25}
            step={1}
            color="#B45309"
            hint="solar PPA · efficiency"
            testid="roi-slider-energy"
          />
          <Slider
            label="Throughput uplift"
            value={throughput}
            onChange={setThroughput}
            max={20}
            step={0.5}
            color="#059669"
            hint="crusher debottleneck"
            testid="roi-slider-throughput"
          />

          <div className="pt-4 border-t border-slate-100">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
              Scenario presets
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { id: "base", label: "Base case" },
                { id: "moderate", label: "Moderate" },
                { id: "ambitious", label: "Ambitious" },
                { id: "stretch", label: "Stretch" },
              ].map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPreset(p.id)}
                  data-testid={`roi-preset-${p.id}`}
                  className="px-3 py-1.5 rounded-full text-[11px] font-semibold border border-slate-200 bg-white text-slate-700 hover:border-slate-900 hover:text-slate-900 transition-colors"
                >
                  {p.label}
                </button>
              ))}
              <button
                onClick={reset}
                data-testid="roi-reset"
                className="px-3 py-1.5 rounded-full text-[11px] font-semibold border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors inline-flex items-center gap-1"
              >
                <RotateCcw size={11} /> Reset
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
              Model assumes 60% variable OpEx, 24% energy share of OpEx, 5.0× EBITDA multiple. Tune in /app/frontend/src/components/RoiCalculator.jsx when real assumptions are loaded.
            </p>
          </div>
        </div>

        {/* Output panel */}
        <div className="xl:col-span-3 bg-gradient-to-br from-slate-900 to-slate-800 rounded-lg p-6 text-white relative overflow-hidden grain">
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-400 font-semibold mb-1">
            Projected outcome
          </div>
          <div className="flex items-baseline gap-3 mb-1">
            <span className="font-display text-5xl xl:text-6xl font-bold text-white tabular-nums" data-testid="roi-valuation">
              {fmtUsd(computed.newValuation)}
            </span>
            <span className={`text-sm font-semibold ${computed.valuationUplift >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              {computed.valuationUplift >= 0 ? "+" : ""}{fmtUsd(computed.valuationUplift)}
            </span>
          </div>
          <div className="text-xs text-slate-400">
            vs base case {fmtUsd(BASE.valuation)} · {BASE.multiplier}× EBITDA multiple
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-6 border-t border-white/10">
            <Outcome label="Revenue" icon={Banknote} value={fmtUsd(computed.newRevenue)} delta={computed.newRevenue - BASE.revenue} testid="roi-revenue" />
            <Outcome label="OpEx" icon={Banknote} value={fmtUsd(computed.newOpex)} delta={computed.newOpex - BASE.opex} invert testid="roi-opex" />
            <Outcome label="EBITDA" icon={TrendingUp} value={fmtUsd(computed.newEbitda)} delta={computed.ebitdaUplift} testid="roi-ebitda" highlight />
            <Outcome label="Margin" icon={Percent} value={`${computed.newMargin.toFixed(1)}%`} delta={computed.newMargin - BASE.margin} unit="pts" testid="roi-margin" />
          </div>

          <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles size={14} className="text-amber-400" />
              <span className="text-xs text-slate-300">Implied IRR (10-yr hold, base case)</span>
            </div>
            <span className="font-display text-2xl font-bold text-amber-400 tabular-nums" data-testid="roi-irr">
              {computed.newIrr.toFixed(1)}%
            </span>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

function Outcome({ label, icon: Icon, value, delta, unit = "", invert = false, highlight = false, testid }) {
  const isGood = invert ? delta < 0 : delta > 0;
  const deltaText = `${delta >= 0 ? "+" : ""}${unit ? delta.toFixed(1) : (Math.abs(delta) >= 1000 ? `R${(delta/1000).toFixed(2)}bn` : `R${delta.toFixed(0)}m`)}${unit}`;
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-amber-400 font-semibold mb-1">
        <Icon size={10} />
        {label}
      </div>
      <div className={`font-display font-bold tabular-nums ${highlight ? "text-3xl text-amber-400" : "text-xl text-white"}`}>
        {value}
      </div>
      {Math.abs(delta) > 0.05 && (
        <div className={`text-[11px] font-semibold mt-0.5 ${isGood ? "text-emerald-400" : "text-red-400"}`}>
          {deltaText}
        </div>
      )}
    </div>
  );
}
